import { createFileRoute, notFound, Link, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import {
  Star, MapPin, Heart, Share2, Languages, Calendar,
  Sparkles, ChefHat, CheckCircle2, ArrowLeft, FileText, Image as ImageIcon, X,
  MessageCircle, ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { chefQueries } from "@/lib/chefs.queries";
import { ReviewsSection } from "@/components/reviews-section";
import { ContactChefDialog } from "@/components/contact-chef-dialog";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { TierBadge, VerifiedBadge, FounderBadge } from "@/components/chef-badges";
import { toast } from "sonner";

export const Route = createFileRoute("/chef/$slug")({
  loader: async ({ params, context }) => {
    // Slug malformato (caratteri non ammessi dal regex pubblico) → 404, mai 500.
    // Il validator zod del server-fn lancerebbe altrimenti un'eccezione
    // generica trasformata dall'errorComponent in "Impossibile caricare".
    if (!/^[a-z0-9-]{1,80}$/i.test(params.slug)) {
      throw notFound();
    }
    const data = await context.queryClient.ensureQueryData(chefQueries.bySlug(params.slug));
    if (!data) throw notFound();
    return data;
  },
  head: ({ loaderData }) => {
    const chef = loaderData?.chef;
    if (!chef) return { meta: [{ title: "Chef non trovato — ChefConnect" }] };
    const where = [chef.city, chef.region].filter(Boolean).join(", ") || "Italia";
    const headline = chef.headline ?? "Chef privato";
    const title = `${chef.display_name} — ${headline} a ${chef.city ?? "Italia"} | ChefConnect`;
    const desc = `${headline}. ${chef.years_experience ? chef.years_experience + " anni di esperienza. " : ""}${chef.rating_count > 0 ? chef.rating_avg.toFixed(1) + "★ su " + chef.rating_count + " recensioni. " : ""}${chef.min_price ? "Da €" + Math.round(chef.min_price) + " a persona." : ""}`.trim();
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: `${chef.display_name} — Chef privato a ${chef.city ?? "Italia"}` },
        { property: "og:description", content: desc },
        { property: "og:type", content: "profile" },
        ...(chef.cover_url ? [{ property: "og:image", content: chef.cover_url }, { name: "twitter:image", content: chef.cover_url }] : []),
        { property: "og:url", content: `https://chefconnectitalia.com/chef/${chef.slug}` },
      ],
      links: [{ rel: "canonical", href: `https://chefconnectitalia.com/chef/${chef.slug}` }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Person",
            name: chef.display_name,
            jobTitle: "Personal Chef",
            ...(chef.cover_url ? { image: chef.cover_url } : {}),
            address: { "@type": "PostalAddress", addressLocality: chef.city ?? undefined, addressRegion: chef.region ?? undefined, addressCountry: "IT" },
            knowsLanguage: chef.languages,
            ...(chef.rating_count > 0 ? {
              aggregateRating: { "@type": "AggregateRating", ratingValue: chef.rating_avg, reviewCount: chef.rating_count },
            } : {}),
            ...(where ? { areaServed: where } : {}),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://chefconnectitalia.com/" },
              { "@type": "ListItem", position: 2, name: "Chef", item: "https://chefconnectitalia.com/search" },
              { "@type": "ListItem", position: 3, name: chef.display_name, item: `https://chefconnectitalia.com/chef/${chef.slug}` },
            ],
          }),
        },
      ],
    };
  },
  component: ChefDetailPage,
  notFoundComponent: ChefNotFound,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="container mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="font-display text-3xl">Impossibile caricare lo chef</h1>
        <p className="mt-3 text-sm text-muted-foreground">{error.message}</p>
        <Button onClick={() => { router.invalidate(); reset(); }} className="mt-6">Riprova</Button>
      </div>
    );
  },
});

function ChefNotFound() {
  return (
    <div className="container mx-auto max-w-2xl px-4 py-24 text-center">
      <p className="text-xs uppercase tracking-[0.2em] text-gold">404</p>
      <h1 className="mt-3 font-display text-4xl">Chef non trovato</h1>
      <p className="mt-3 text-muted-foreground">Lo chef che cerchi non esiste o non è più disponibile.</p>
      <Button asChild className="mt-8"><Link to="/search">Esplora altri chef</Link></Button>
    </div>
  );
}

function ChefDetailPage() {
  const { slug } = Route.useParams();
  const { data } = useSuspenseQuery(chefQueries.bySlug(slug));
  const chef = data!.chef;
  const media = data!.media;
  const whatsapp = data!.whatsapp;
  const photos = media.filter((m) => m.kind === "photo" || m.kind === "cover").map((m) => m.url);
  const videos = media.filter((m) => m.kind === "video").map((m) => m.url);
  const menus = media.filter((m) => m.kind === "menu").map((m) => m.url);
  const avatarUrl = media.find((m) => m.kind === "avatar")?.url ?? null;
  const heroImages = chef.cover_url ? [chef.cover_url, ...photos.filter((u) => u !== chef.cover_url)] : photos;
  const [activeImage, setActiveImage] = useState(0);
  const [saved, setSaved] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Telemetria: profile_view (1 sola riga / visitatore / giorno via dedup server-side)
  useEffect(() => {
    if (!chef?.user_id) return;
    import("@/lib/analytics").then(({ track }) =>
      track("profile_view", chef.user_id, {
        city: chef.city ?? undefined,
        tier: (chef as { premium_tier?: "free" | "silver" | "gold" }).premium_tier ?? undefined,
      }),
    );
  }, [chef?.user_id, chef?.city]);

  const handleShare = async () => {
    const url = typeof window !== "undefined" ? window.location.href : `https://chefconnectitalia.com/chef/${chef.slug}`;
    const shareData = {
      title: `${chef.display_name} — ChefConnect`,
      text: chef.headline ?? `Chef privato${chef.city ? " a " + chef.city : ""}`,
      url,
    };
    try {
      if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
        await navigator.share(shareData);
        return;
      }
      await navigator.clipboard.writeText(url);
      toast.success("Link copiato negli appunti");
    } catch (err) {
      // utente ha annullato lo share nativo: silenzio
      if ((err as { name?: string })?.name === "AbortError") return;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Link copiato negli appunti");
      } catch {
        toast.error("Impossibile condividere il link");
      }
    }
  };

  const priceFrom = chef.min_price ?? chef.hourly_rate;
  const initials = chef.display_name.split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase() ?? "").join("");

  return (
    <article className="animate-fade-in">
      <section className="container mx-auto max-w-7xl px-4 pt-8 sm:px-6">
        <Link to="/search" className="inline-flex items-center gap-1 text-sm text-muted-foreground transition hover:text-foreground">
          <ArrowLeft className="h-3.5 w-3.5" /> Torna alla ricerca
        </Link>

        <div className="relative mt-6 grid gap-3 sm:grid-cols-[2fr_1fr] sm:grid-rows-2">
          <button
            type="button"
            onClick={() => heroImages[activeImage] && setLightboxIndex(activeImage)}
            aria-label={heroImages[activeImage] ? "Apri galleria" : "Nessuna foto disponibile"}
            disabled={!heroImages[activeImage]}
            className="row-span-2 overflow-hidden rounded-xl bg-muted text-left"
          >
            {heroImages[activeImage] ? (
              <img src={heroImages[activeImage]} alt={`${chef.display_name} — ${chef.headline ?? "Chef privato"}`} fetchPriority="high" className="h-full max-h-[560px] w-full object-cover" />
            ) : (
              <div className="grid aspect-[3/2] w-full place-items-center bg-gradient-to-br from-secondary to-muted">
                <span className="font-display text-7xl text-muted-foreground/50">{initials || "👨‍🍳"}</span>
              </div>
            )}
          </button>
          {heroImages.slice(0, 2).map((img, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setActiveImage(i)}
              aria-label={`Mostra foto ${i + 1} di ${chef.display_name}`}
              aria-pressed={activeImage === i}
              className={`overflow-hidden rounded-xl bg-muted transition ${activeImage === i ? "ring-2 ring-gold" : "opacity-90 hover:opacity-100"}`}
            >
              <img src={img} alt="" loading="lazy" className="aspect-[4/3] h-full w-full object-cover" />
            </button>
          ))}
          {heroImages.length > 1 && (
            <button
              type="button"
              onClick={() => setLightboxIndex(activeImage)}
              className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-background/90 px-3 py-1.5 text-xs font-medium shadow-sm backdrop-blur transition hover:bg-background"
            >
              <ImageIcon className="h-3.5 w-3.5" />
              {heroImages.length > 3 ? `+ ${heroImages.length - 3} foto` : `Vedi tutte (${heroImages.length})`}
            </button>
          )}
        </div>
      </section>

      <section className="container mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16">
        <div className="grid gap-12 lg:grid-cols-[1fr_360px]">
          <div>
            <div className="flex flex-wrap items-start gap-5">
              {avatarUrl ? (
                <img src={avatarUrl} alt={`Foto profilo di ${chef.display_name}`} className="h-20 w-20 shrink-0 rounded-full object-cover ring-4 ring-card" />
              ) : (
                <div aria-hidden="true" className="grid h-20 w-20 shrink-0 place-items-center rounded-full bg-secondary font-display text-2xl ring-4 ring-card">
                  {initials || "C"}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="font-display text-4xl sm:text-5xl">{chef.display_name}</h1>
                  {chef.verified_badge && <VerifiedBadge />}
                  <TierBadge tier={chef.premium_tier} />
                  {chef.founder_badge && <FounderBadge />}
                </div>
                {chef.headline && <p className="mt-1 text-lg text-muted-foreground">{chef.headline}</p>}
                <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
                  {chef.rating_count > 0 ? (
                    <span className="inline-flex items-center gap-1.5">
                      <Star className="h-4 w-4 fill-gold text-gold" />
                      <span className="font-medium text-foreground">{chef.rating_avg.toFixed(1)}</span>
                      <span>({chef.rating_count} recensioni)</span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
                      <Sparkles className="h-4 w-4 text-gold" /> Nuovo su ChefConnect
                    </span>
                  )}
                  {chef.city && (
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin className="h-4 w-4" /> {chef.city}{chef.region ? `, ${chef.region}` : ""}
                    </span>
                  )}
                  {chef.years_experience != null && (
                    <span className="inline-flex items-center gap-1.5">
                      <ChefHat className="h-4 w-4" /> {chef.years_experience} anni di esperienza
                    </span>
                  )}
                  {chef.created_at && (
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar className="h-4 w-4" /> Su ChefConnect da {new Date(chef.created_at).toLocaleDateString("it-IT", { month: "long", year: "numeric" })}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {videos.length > 0 && (
              <>
                <div className="mt-10 gold-divider"><h2 className="font-display text-2xl">Video di presentazione</h2></div>
                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  {videos.map((src) => (
                    <video
                      key={src}
                      src={src}
                      controls
                      muted
                      playsInline
                      preload="metadata"
                      className="aspect-video w-full rounded-xl bg-black shadow-sm"
                      onError={(e) => {
                        // Nasconde il singolo video se la sorgente non è più disponibile (link rotti).
                        e.currentTarget.style.display = "none";
                      }}
                    >
                      <track kind="captions" />
                    </video>
                  ))}
                </div>
              </>
            )}

            {chef.bio && (
              <>
                <div className="mt-10 gold-divider"><h2 className="font-display text-2xl">Chi sono</h2></div>
                <p className="mt-5 text-lg leading-relaxed text-muted-foreground whitespace-pre-wrap">{chef.bio}</p>
              </>
            )}

            {/* Perché scegliere questo chef — sezione fiducia, riusa dati esistenti */}
            <div className="mt-12 gold-divider"><h2 className="font-display text-2xl">Perché scegliere {chef.display_name.split(/\s+/)[0]}</h2></div>
            <ul className="mt-5 grid gap-3 sm:grid-cols-2">
              {chef.verified_badge && (
                <TrustItem icon={ShieldCheck} text="Profilo verificato dal nostro team" />
              )}
              {chef.years_experience != null && chef.years_experience > 0 && (
                <TrustItem icon={ChefHat} text={`${chef.years_experience} anni di esperienza`} />
              )}
              {chef.specialties.length > 0 && (
                <TrustItem icon={Sparkles} text={`Specialità: ${chef.specialties.slice(0, 3).join(", ")}`} />
              )}
              {chef.languages.length > 0 && (
                <TrustItem icon={Languages} text={`Parla ${chef.languages.join(", ")}`} />
              )}
              {chef.rating_count > 0 && (
                <TrustItem icon={Star} text={`${chef.rating_avg.toFixed(1)}★ su ${chef.rating_count} recensioni`} />
              )}
            </ul>

            {menus.length > 0 && (
              <>
                <div className="mt-12 gold-divider"><h2 className="font-display text-2xl">Menù</h2></div>
                <ul className="mt-5 space-y-2">
                  {menus.map((url, i) => (
                    <li key={url}>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-3 text-sm transition hover:bg-secondary"
                      >
                        <FileText className="h-4 w-4 text-gold" />
                        Menù {menus.length > 1 ? i + 1 : ""} (PDF)
                      </a>
                    </li>
                  ))}
                </ul>
              </>
            )}

            <div className="mt-12 grid gap-8 sm:grid-cols-2">
              {chef.cuisines.length > 0 && (
                <Detail icon={Sparkles} title="Cucine">
                  <div className="flex flex-wrap gap-1.5">
                    {chef.cuisines.map((c) => (<span key={c} className="rounded-full bg-secondary px-3 py-1 text-xs">{c}</span>))}
                  </div>
                </Detail>
              )}
              {chef.specialties.length > 0 && (
                <Detail icon={CheckCircle2} title="Specialità">
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    {chef.specialties.map((s) => (
                      <li key={s} className="flex items-start gap-2"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gold" /> {s}</li>
                    ))}
                  </ul>
                </Detail>
              )}
              {chef.languages.length > 0 && (
                <Detail icon={Languages} title="Lingue">
                  <p className="text-sm text-muted-foreground">{chef.languages.join(" · ")}</p>
                </Detail>
              )}
              {(chef.city || chef.region) && (
                <Detail icon={MapPin} title="Zona servita">
                  <p className="text-sm text-muted-foreground">{[chef.city, chef.region].filter(Boolean).join(", ")}</p>
                </Detail>
              )}
            </div>

            <div className="mt-14 gold-divider"><h2 className="font-display text-2xl">Disponibilità</h2></div>
            <div className="mt-5 rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
              <Calendar className="mr-2 inline h-4 w-4 text-gold" />
              Per verificare la disponibilità e ricevere un preventivo, invia una richiesta di contatto: lo chef ti risponderà appena disponibile.
            </div>

            <div className="mt-14 gold-divider"><h2 className="font-display text-2xl">Recensioni</h2></div>
            <ReviewsSection slug={chef.slug} />

            {/* Cosa succede dopo la richiesta — timeline rassicurante */}
            <div className="mt-14 gold-divider"><h2 className="font-display text-2xl">Cosa succede dopo la richiesta?</h2></div>
            <ol className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { n: 1, t: "Invii gratuitamente la richiesta." },
                { n: 2, t: "Lo chef ti contatta." },
                { n: 3, t: "Definite insieme il menù." },
                { n: 4, t: "Ti godi l'esperienza." },
              ].map((s) => (
                <li key={s.n} className="flex flex-col gap-3 rounded-xl border border-border bg-card p-5">
                  <span className="grid h-9 w-9 place-items-center rounded-full bg-gold text-gold-foreground font-display text-base">
                    {s.n}
                  </span>
                  <p className="text-sm text-foreground">{s.t}</p>
                </li>
              ))}
            </ol>
          </div>

          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
              {priceFrom != null ? (
                <>
                  <div className="flex items-baseline gap-1">
                    <span className="text-xs text-muted-foreground">da</span>
                    <span className="font-display text-4xl">€{Math.round(priceFrom)}</span>
                    <span className="text-sm text-muted-foreground">a persona</span>
                  </div>
                  {chef.max_price != null && (
                    <p className="mt-1 text-xs text-muted-foreground">Fino a €{Math.round(chef.max_price)} a persona</p>
                  )}
                </>
              ) : (
                <p className="font-display text-lg">Preventivo su richiesta</p>
              )}

              <div className="mt-6 space-y-2">
                <ContactChefDialog chefSlug={chef.slug} chefName={chef.display_name} variant="primary" />
                {whatsapp && (
                  <div>
                    <a
                      href={`https://wa.me/${whatsapp.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Ciao ${chef.display_name}, ti contatto da ChefConnect.`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        import("@/lib/analytics").then(({ track }) =>
                          track("whatsapp_click", chef.user_id, { city: chef.city ?? undefined }),
                        );
                      }}
                      className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md bg-emerald-600 px-4 text-sm font-medium text-white shadow-sm transition hover:bg-emerald-700"
                    >
                      <MessageCircle className="h-4 w-4" /> Contatta su WhatsApp
                    </a>
                    <p className="mt-1.5 text-center text-xs text-muted-foreground">Risposta direttamente dallo chef.</p>
                  </div>
                )}
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2">
                <Button variant="ghost" size="sm" onClick={() => setSaved((s) => !s)} aria-pressed={saved} aria-label={saved ? "Rimuovi dai preferiti" : "Salva nei preferiti"} className="justify-center">
                  <Heart className={`h-4 w-4 ${saved ? "fill-gold text-gold" : ""}`} />
                  {saved ? "Salvato" : "Salva"}
                </Button>
                <Button variant="ghost" size="sm" onClick={handleShare} aria-label="Condividi il profilo dello chef" className="justify-center">
                  <Share2 className="h-4 w-4" /> Condividi
                </Button>
              </div>

              <p className="mt-5 border-t border-border pt-4 text-center text-xs text-muted-foreground">
                Contatti gratis. Paghi solo lo chef, senza intermediari.
              </p>
            </div>
          </aside>
        </div>
      </section>

      {/* Mobile sticky CTA: sempre raggiungibile su schermi piccoli */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.05)] backdrop-blur lg:hidden">
        <div className="mx-auto flex max-w-7xl items-center gap-3">
          <div className="min-w-0 flex-1">
            {priceFrom != null ? (
              <div className="flex items-baseline gap-1">
                <span className="text-xs text-muted-foreground">da</span>
                <span className="font-display text-xl">€{Math.round(priceFrom)}</span>
                <span className="text-xs text-muted-foreground">/persona</span>
              </div>
            ) : (
              <p className="text-sm font-medium">Preventivo su richiesta</p>
            )}
          </div>
          <div className="shrink-0">
            <ContactChefDialog chefSlug={chef.slug} chefName={chef.display_name} variant="primary" />
          </div>
        </div>
      </div>
      <div aria-hidden="true" className="h-20 lg:hidden" />

      {/* Lightbox galleria */}
      <Dialog open={lightboxIndex !== null} onOpenChange={(o) => !o && setLightboxIndex(null)}>
        <DialogContent className="max-w-5xl border-none bg-background/95 p-0 backdrop-blur">
          {lightboxIndex !== null && heroImages[lightboxIndex] && (
            <div className="relative">
              <img
                src={heroImages[lightboxIndex]}
                alt={`${chef.display_name} — foto ${lightboxIndex + 1} di ${heroImages.length}`}
                className="max-h-[85vh] w-full object-contain"
              />
              <button
                type="button"
                onClick={() => setLightboxIndex(null)}
                aria-label="Chiudi galleria"
                className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-background/80 backdrop-blur transition hover:bg-background"
              >
                <X className="h-4 w-4" />
              </button>
              {heroImages.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => setLightboxIndex((i) => (i === null ? 0 : (i - 1 + heroImages.length) % heroImages.length))}
                    aria-label="Foto precedente"
                    className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-background/80 px-3 py-2 text-sm backdrop-blur transition hover:bg-background"
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    onClick={() => setLightboxIndex((i) => (i === null ? 0 : (i + 1) % heroImages.length))}
                    aria-label="Foto successiva"
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-background/80 px-3 py-2 text-sm backdrop-blur transition hover:bg-background"
                  >
                    ›
                  </button>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-background/80 px-3 py-1 text-xs backdrop-blur">
                    {lightboxIndex + 1} / {heroImages.length}
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </article>
  );
}

function Detail({
  icon: Icon, title, children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h3 className="inline-flex items-center gap-2 text-sm font-medium uppercase tracking-wider text-muted-foreground">
        <Icon className="h-4 w-4 text-gold" /> {title}
      </h3>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function TrustItem({ icon: Icon, text }: { icon: React.ComponentType<{ className?: string }>; text: string }) {
  return (
    <li className="flex items-start gap-3 rounded-xl border border-border bg-card p-4 transition hover:border-gold/50">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-gold-muted/40 text-gold">
        <Icon className="h-4 w-4" />
      </span>
      <span className="pt-1.5 text-sm text-foreground">{text}</span>
    </li>
  );
}